import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { randomboxApi } from '../../api/randombox.js';
import { ApiError } from '../../api/client.js';
import type { RbPrize, RbPrizeInput, RbProduct } from '../../api/types.js';
import { ErrorBox, Field, SafeLink } from '../../components/ui.js';
import { Modal, ConfirmModal } from '../../components/Modal.js';
import { QueryState } from '../../components/QueryState.js';
import { GradeBadge } from '../../components/GradeBadge.js';
import { text, money, num, datetime, dateOnly } from '../../lib/format.js';
import { checkImageUrl, checkLen, hasFieldErrors, inputCls, type FieldErrors } from '../../lib/formValidation.js';

const GRADES = ['1', '2', '3', '4', '5', '6', '7'];
const UNITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

/**
 * 원본: randombox/product/prize_list.php
 *
 * lottery 와 달리 이력 테이블(lottery_prizes_log 같은 것)이 없다.
 * 그리고 재고는 cron_randombox_quantity_update.php 가 banana.shop_goods 에서
 * 주기적으로 덮어쓴다 - 화면에서 그 사실을 알려준다.
 */
export function RbPrizesPage() {
  const { randomboxId: raw = '' } = useParams();
  const randomboxId = Number(raw);
  const queryClient = useQueryClient();

  const [formModal, setFormModal] = useState<{ mode: 'edit' | 'copy'; prize: RbPrize } | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState<number | null>(null);

  const product = useQuery({
    queryKey: ['randombox', 'product', randomboxId],
    queryFn: () => randomboxApi.product(randomboxId),
    enabled: Number.isFinite(randomboxId) && randomboxId > 0,
  });

  const prizes = useQuery({
    queryKey: ['randombox', 'prizes', randomboxId],
    queryFn: () => randomboxApi.prizes(randomboxId),
    enabled: Number.isFinite(randomboxId) && randomboxId > 0,
  });

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['randombox', 'prizes', randomboxId] });
  }

  const remove = useMutation({
    mutationFn: (prizeId: number) => randomboxApi.deletePrize(randomboxId, prizeId),
    onSuccess: () => {
      setConfirmingDelete(null);
      refresh();
    },
  });

  if (!Number.isFinite(randomboxId) || randomboxId <= 0) {
    return <ErrorBox error={new Error('잘못된 랜덤박스 ID 입니다.')} />;
  }

  const prizeItems = prizes.data?.items ?? [];

  return (
    <section className="lottery-prize-page randombox-prize-page">
      {/*
        머리글은 다른 세 화면과 같은 모양으로 맞춘다. 이 화면만 eyebrow 문구와
        설명줄을 따로 갖고 있어서, 같은 메뉴 안을 오갈 때 다른 시스템처럼
        보였다. 랜덤박스 번호는 제목 옆 부제로 옮긴다.
      */}
      <header className="page-head">
        <div>
          <Link className="link link--back" to="/randombox/products">
            ← 랜덤박스 목록
          </Link>
          <h1 className="page-title">
            경품 관리
            <span className="page-title__sub">#{randomboxId}</span>
          </h1>
        </div>
        {prizes.isFetching && <span className="page-head__busy">갱신 중…</span>}
      </header>

      {product.data && <RandomboxProductSummary product={product.data} randomboxId={randomboxId} />}

      <aside className="randombox-sync-notice">
        <span aria-hidden="true">↻</span>
        <p>
          <strong>재고 자동 동기화</strong>
          매시간 12분에 비즈호스트 재고를 반영합니다. 품절 상품과 7등급은 제외되며, 직접 수정한
          재고는 다음 배치에서 덮어써질 수 있습니다. 변경 기록은 서버 로그에만 남습니다.
        </p>
      </aside>

      <div className="lottery-prize-section randombox-prize-section">
        <div className="lottery-prize-section__head">
          <div>
            <p className="lottery-prize-section__eyebrow">GRADE INVENTORY</p>
            <h2 className="lottery-prize-section__title">세부 상품 목록</h2>
            <p className="randombox-prize-section__description">각 등급별 상품 구성과 현재 재고를 확인합니다.</p>
          </div>
          {prizes.data && (
            <div className="lottery-prize-stats" aria-label="경품 요약">
              <span><b>{num(prizeItems.length)}</b>개 상품</span>
              <span><b>{num(prizes.data.totalQuantity)}</b>개 재고</span>
              <span className={prizes.data.soldoutCount > 0 ? 'lottery-prize-stats__warn' : undefined}>
                <b>{num(prizes.data.soldoutCount)}</b>개 품절
              </span>
            </div>
          )}
        </div>

        <QueryState
          query={prizes}
          empty={prizeItems.length === 0}
          emptyMessage="등록된 경품이 없습니다."
        >
          <div className="table-wrap lottery-prize-table-wrap">
            <table className="table lottery-prize-table randombox-prize-table">
              <thead>
                <tr>
                  <th>No.</th>
                  <th>등급</th>
                  <th>당첨용 이미지</th>
                  <th>표시용 이미지</th>
                  <th>코드 / 바코드</th>
                  <th>상품명</th>
                  <th style={{ textAlign: 'right' }}>수량</th>
                  <th>등록일</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {prizeItems.map((prize, i) => (
                  <PrizeRow
                    key={prize.prizeId ?? i}
                    index={i + 1}
                    randomboxId={randomboxId}
                    prize={prize}
                    onEdit={() => setFormModal({ mode: 'edit', prize })}
                    onCopy={() => setFormModal({ mode: 'copy', prize })}
                    onAskDelete={() => setConfirmingDelete(prize.prizeId)}
                    onSaved={refresh}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </QueryState>
      </div>

      {confirmingDelete !== null && (
        <ConfirmModal
          title="경품 삭제"
          message={
            <>
              경품 <b>{text(prizeItems.find((p) => p.prizeId === confirmingDelete)?.productName)}</b>
              을(를) 지웁니다. 되돌릴 수 없습니다.
            </>
          }
          pending={remove.isPending}
          error={remove.isError ? <ErrorBox error={remove.error} /> : undefined}
          onConfirm={() => remove.mutate(confirmingDelete)}
          onClose={() => {
            setConfirmingDelete(null);
            remove.reset();
          }}
        />
      )}

      {formModal && (
        <PrizeFormModal
          mode={formModal.mode}
          randomboxId={randomboxId}
          prize={formModal.prize}
          onClose={() => setFormModal(null)}
          onSaved={() => {
            setFormModal(null);
            refresh();
          }}
        />
      )}
    </section>
  );
}

function RandomboxProductSummary({ product, randomboxId }: { product: RbProduct; randomboxId: number }) {
  return (
    <article className="lottery-product-summary randombox-product-summary">
      <div className="lottery-product-summary__media">
        {product.thumbnail ? (
          <SafeLink href={product.thumbnail} title="원본 이미지 열기">
            <img src={product.thumbnail} alt="" />
          </SafeLink>
        ) : (
          <span>NO IMAGE</span>
        )}
      </div>

      <div className="lottery-product-summary__body">
        <div className="lottery-product-summary__title-row">
          <div>
            <span className="lottery-product-summary__label">랜덤박스 번호 {randomboxId}</span>
            <h2>{text(product.name)}</h2>
            <p>{text(product.categoryName)}</p>
          </div>
          <strong className="lottery-product-summary__price">
            {product.price === null ? '-' : `${money(product.price)}원`}
          </strong>
        </div>

        <dl className="lottery-product-summary__facts">
          <div>
            <dt>판매 기간</dt>
            <dd>{dateOnly(product.saleStart)} ~ {dateOnly(product.saleEnd)}</dd>
          </div>
          <div>
            <dt>발송일</dt>
            <dd>{dateOnly(product.deliveryDate)}</dd>
          </div>
          <div>
            <dt>노출 순위</dt>
            <dd>{num(product.priorityRank)}</dd>
          </div>
          <div>
            <dt>등록일</dt>
            <dd>{dateOnly(product.regdate)}</dd>
          </div>
        </dl>
      </div>

      <div className="lottery-product-summary__memo">
        <span>관리용 메모</span>
        <p>{text(product.memo)}</p>
        {product.eventMemo && <small>이벤트 메모 · {product.eventMemo}</small>}
      </div>
    </article>
  );
}

/** 원본 prize_list.php 의 당첨용/표시용 이미지 칸 - 클릭하면 원본 이미지를 새 탭으로 연다. */
function PrizeImageCell({ src, label }: { src: string | null; label: string }) {
  if (!src) return <span className="lottery-prize-table__image-empty">NO IMAGE</span>;
  return (
    <SafeLink href={src} title={`${label} 원본 열기`}>
      <img className="lottery-prize-table__image" src={src} alt="" loading="lazy" />
    </SafeLink>
  );
}

/** 받은 이미지를 파일로 내려받게 한다 (LookupPage 의 CSV 내려받기와 같은 방식). */
function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * '제작1'·'제작2' — 원본 prize_list.php:519-537 의 두 버튼.
 *
 * 원본은 make_thumbnail.php / make_display.php 를 iframe 모달로 열었고, 그
 * PHP 가 이미지를 곧바로 다운로드로 내려보냈다(모달에는 아무것도 안 보였다).
 * 여기서는 모달 없이 바로 내려받는다 - 결과 파일은 원본과 같다.
 *
 * 만들어진 이미지는 어디에도 저장되지 않는다. 그래서 여러 번 눌러도 데이터가
 * 중복되거나 덮어써지지 않는다 - 연타로 외부 API 를 두드리는 것만 막으면 된다.
 */
function PrizeMakeButtons({ randomboxId, prize }: { randomboxId: number; prize: RbPrize }) {
  const [kind, setKind] = useState<'rank' | 'display' | null>(null);

  const make = useMutation({
    mutationFn: async (next: 'rank' | 'display') => {
      if (prize.prizeId === null) throw new Error('경품 ID 가 없습니다.');
      setKind(next);
      const blob = await randomboxApi.makePrizeImage(randomboxId, prize.prizeId, next);
      const suffix = next === 'rank' ? '_t' : '_d';
      triggerDownload(blob, `randombox_${prize.prizeCode ?? prize.prizeId}${suffix}.png`);
    },
    onSettled: () => setKind(null),
  });

  const busy = make.isPending;

  return (
    <div className="randombox-prize-make">
      <button
        type="button"
        className="btn btn--sm randombox-prize-action"
        disabled={busy}
        title="원본 상품 이미지로 당첨용(랭킹) 이미지를 만들어 내려받습니다"
        onClick={() => make.mutate('rank')}
      >
        {busy && kind === 'rank' ? '제작 중…' : '제작1'}
      </button>
      <button
        type="button"
        className="btn btn--sm randombox-prize-action"
        disabled={busy}
        title="원본 상품 이미지로 표시용(디스플레이) 이미지를 만들어 내려받습니다"
        onClick={() => make.mutate('display')}
      >
        {busy && kind === 'display' ? '제작 중…' : '제작2'}
      </button>
      {make.isError && (
        <small className="randombox-prize-make__error">
          {make.error instanceof ApiError ? make.error.message : '이미지 제작에 실패했습니다.'}
        </small>
      )}
    </div>
  );
}

/**
 * 재고 한 칸 (상품 1 또는 상품 2).
 *
 * 상품이 둘 붙은 경품은 이 칸이 둘 생긴다(2026-10-02). 각 칸은 자기 컬럼만
 * PATCH 해서 - 서버도 보내온 칸만 compare-and-set 한다 - 한쪽을 고치는 동안
 * 다른 쪽 값이 덮여 사라지지 않는다.
 */
function QuantityInput({ randomboxId, prize, field, label, onSaved }: {
  randomboxId: number;
  prize: RbPrize;
  field: 'quantity' | 'quantity2';
  /** 칸이 둘일 때만 붙이는 이름. 하나뿐이면 예전처럼 라벨 없이 그린다. */
  label?: string | undefined;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const current = (field === 'quantity' ? prize.quantity : prize.quantity2) ?? 0;
  const editing = draft !== null;

  const mutation = useMutation({
    mutationFn: (quantity: number) => {
      if (prize.prizeId === null) throw new Error('경품 ID 가 없습니다.');
      return randomboxApi.updatePrizeQuantity(randomboxId, prize.prizeId, quantity, field);
    },
    onSuccess: () => {
      setDraft(null);
      onSaved();
    },
  });

  const conflict = mutation.error instanceof ApiError && mutation.error.status === 409;
  // label 은 보조기술이 읽는 전체 이름("상품 1 수량")이고, 눈에 보이는 쪽은
  // 번호칩 + '수량' 이다 - 칩이 위 '코드' 칸의 ①②바코드와 짝을 지어 준다.
  const name = label ?? '재고';
  const no = field === 'quantity' ? 1 : 2;

  return (
    <div className="randombox-prize-qty">
      {label && (
        <span className="randombox-prize-qty__label">
          <i className="randombox-prize-no" aria-hidden="true">{no}</i>
          수량
        </span>
      )}
      <div className="qty">
        <input
          className="input input--qty"
          type="number"
          min={0}
          value={editing ? draft : String(current)}
          onChange={(e) => setDraft(e.target.value)}
          disabled={mutation.isPending || prize.prizeId === null}
          aria-label={`${text(prize.prizeCode)} ${name}`}
        />
        {editing && (
          <>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={mutation.isPending}
              onClick={() => {
                const value = Number(draft);
                if (!Number.isFinite(value) || value < 0) return;
                mutation.mutate(value);
              }}
            >
              {mutation.isPending ? '…' : '저장'}
            </button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setDraft(null)}>
              취소
            </button>
          </>
        )}
      </div>
      {mutation.isError && (
        <div className={conflict ? 'qty-msg qty-msg--conflict' : 'qty-msg qty-msg--error'}>
          {mutation.error instanceof Error ? mutation.error.message : '저장 실패'}
          {conflict && (
            <button
              type="button"
              className="link"
              onClick={() => {
                setDraft(null);
                mutation.reset();
                onSaved();
              }}
            >
              새로고침
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function PrizeRow(props: {
  index: number;
  randomboxId: number;
  prize: RbPrize;
  onEdit: () => void;
  onCopy: () => void;
  onAskDelete: () => void;
  onSaved: () => void;
}) {
  const { prize } = props;
  const current = prize.quantity ?? 0;

  const flagged = prize.soldoutFlag === '1' || prize.soldoutFlag === 'Y';
  // 상품이 둘이면 하나만 떨어져도 품절이다 - 판정은 서버가 내려준 값을 그대로
  // 쓴다(화면이 따로 계산하면 배치·API 와 조건이 어긋난다).
  const soldout = flagged || prize.soldoutByStock;
  const lowStock = !soldout && current <= 5 && prize.grade !== '7';
  const goodsUrl = prize.goodsIdx ? `https://www.bananamall.co.kr/shop/view.php?index_no=${prize.goodsIdx}` : null;

  return (
    <tr
      className={[
        // 상품이 둘인 경품은 표에서 바로 눈에 띄게 왼쪽에 띠를 둔다 - 색만으로
        // 알리지 않도록 아래 '상품 2개' 배지와 ①②번호도 함께 붙인다(2026-10-04).
        prize.hasProduct2 ? 'randombox-prize-table__set-row' : null,
        soldout
          ? 'randombox-prize-table__soldout-row'
          : lowStock
            ? 'randombox-prize-table__low-stock-row'
            : null,
      ]
        .filter(Boolean)
        .join(' ') || undefined}
    >
      <td className="td-muted">{props.index}</td>
      <td><GradeBadge grade={prize.grade} palette="prizeList" /></td>
      <td className="lottery-prize-table__image-cell">
        <PrizeImageCell src={prize.thumbnail} label="당첨용 이미지" />
        {/* 원본도 이 칸(당첨용 이미지) 아래에만 제작 버튼을 뒀다. */}
        <PrizeMakeButtons randomboxId={props.randomboxId} prize={prize} />
      </td>
      <td className="lottery-prize-table__image-cell">
        <PrizeImageCell src={prize.displayImg} label="표시용 이미지" />
      </td>
      <td className="randombox-prize-table__codes">
        <code>{text(prize.prizeCode)}</code>
        {/* 상품이 둘인 경품은 글자로도 분명히 알려준다 (색만으로 알리지 않는다). */}
        {prize.hasProduct2 && <span className="badge badge--set">상품 2개</span>}
        {/* 상품코드는 번호라 숫자 서식(천 단위 콤마)을 쓰지 않는다 (2026-10-16). */}
        <span>상품코드: {text(prize.goodsIdx == null ? null : String(prize.goodsIdx))}</span>
        {prize.hasProduct2 ? (
          // 바코드를 ①② 번호와 함께 늘어놓아, 오른쪽 수량 칸의 ①②와 짝이
          // 눈으로 바로 이어지게 한다 (migrations/018 · 019).
          <>
            <span className="randombox-prize-table__pline">
              <i className="randombox-prize-no" aria-hidden="true">1</i>
              바코드: {text(prize.barcode)}
            </span>
            <span className="randombox-prize-table__pline">
              <i className="randombox-prize-no" aria-hidden="true">2</i>
              바코드: {prize.barcode2}
            </span>
          </>
        ) : (
          <span>바코드: {text(prize.barcode)}</span>
        )}
      </td>
      <td className="randombox-prize-table__product">
        {goodsUrl ? (
          <a href={goodsUrl} target="_blank" rel="noreferrer">{text(prize.productName)}</a>
        ) : (
          <strong>{text(prize.productName)}</strong>
        )}
        {soldout && <span className="badge badge--danger">품절</span>}
        {prize.companyName && <small>{prize.companyName}</small>}
        {/*
          2번 상품에는 이름이 없다 (migrations/019 에서 컬럼을 지웠다) - 바코드만
          있어서 위 '코드 / 바코드' 칸의 '바코드2' 줄로만 보여준다.
        */}
        {prize.grade === 'L' && (
          <small className="randombox-prize-table__lucky">
            행운상 이벤트: {dateOnly(prize.luckyStartDate)} ~ {dateOnly(prize.luckyEndDate)}
          </small>
        )}
      </td>
      <td className="td-right lottery-prize-table__quantity">
        {/*
          상품이 둘인 경품은 수량 칸도 둘이다 (2026-10-02). 각 칸은 자기
          컬럼(quantity / quantity2)만 따로 저장하므로 서로를 덮어쓰지 않는다.
          상품이 하나면 칸도 하나 - 예전 화면과 같다.
        */}
        <QuantityInput
          randomboxId={props.randomboxId}
          prize={prize}
          field="quantity"
          label={prize.hasProduct2 ? '상품 1 수량' : undefined}
          onSaved={props.onSaved}
        />
        {prize.hasProduct2 && (
          <QuantityInput
            randomboxId={props.randomboxId}
            prize={prize}
            field="quantity2"
            label="상품 2 수량"
            onSaved={props.onSaved}
          />
        )}
      </td>
      <td className="td-muted lottery-prize-table__date" title={`최종 변경: ${datetime(prize.lastUpdate)}`}>
        {dateOnly(prize.regdate)}
      </td>
      <td className="lottery-prize-table__actions-cell">
        <div className="lottery-prize-actions randombox-prize-actions">
          <button type="button" className="btn btn--sm randombox-prize-action randombox-prize-action--edit" onClick={props.onEdit}>
            <span aria-hidden="true">✎</span>
            수정
          </button>
          <button type="button" className="btn btn--sm randombox-prize-action randombox-prize-action--copy" onClick={props.onCopy}>
            <span aria-hidden="true">⧉</span>
            복사
          </button>
          {/*
            삭제는 확인 모달로 묻는다. 예전에는 이 버튼이 '삭제' → '정말 삭제'
            로 바뀌는 방식이었는데, 바로 옆의 '수정'·'복사' 와 붙어 있어 잘못
            누르기 쉬웠다.
          */}
          <button
            type="button"
            className="btn btn--sm randombox-prize-action randombox-prize-action--delete"
            onClick={props.onAskDelete}
          >
            <span aria-hidden="true">×</span>
            삭제
          </button>
        </div>
      </td>
    </tr>
  );
}


/**
 * 한 경품에 붙는 실물 상품. 최대 2개까지 등록한다 (migrations/018).
 * 2번 상품은 randombox_prizes 의 *2 컬럼 세 개에 담긴다.
 *
 * **2번 상품은 바코드만 입력한다** (2026-09-30). 제작업체명·상품명 입력칸은
 * 뺐다 - 컬럼과 API 는 그대로 두고(빈 문자열이 간다) 화면에서만 없앤 것이다.
 * 바코드로 원본 상품을 찾는 기존 로직(배송 엑셀의 bizhostExport.service.ts,
 * 이미지 제작의 prizeImage.service.ts 조인)은 건드리지 않았다.
 */
interface PrizeProductForm {
  barcode: string;
  companyName: string;
  productName: string;
}

const MAX_PRIZE_PRODUCTS = 2;

const EMPTY_PRODUCT: PrizeProductForm = { barcode: '', companyName: '', productName: '' };

/** 컬럼 폭과 같은 값이다 (server/modules/randombox/index.ts 의 필드 정의와 짝). */
const PRODUCT_LIMITS = { barcode: 50, companyName: 50, productName: 255 } as const;

interface PrizeFormState {
  grade: string;
  unit: string;
  quantity: string;
  /** 2번 상품 재고. 2번 상품을 안 쓰면 저장할 때 null 로 보낸다(2026-10-02). */
  quantity2: string;
  soldout: boolean;
  prizeCode: string;
  goodsIdx: string;
  thumbnail: string;
  displayImg: string;
  /** 1번 상품은 항상 있고, 2번은 값이 있거나 '+' 를 눌렀을 때만 들어 있다. */
  products: PrizeProductForm[];
}

/** 2번 상품은 바코드뿐이라(migrations/019) 바코드만 보면 된다. */
const hasProductValue = (p: PrizeProductForm) => p.barcode.trim() !== '';

function toPrizeFormState(p: RbPrize): PrizeFormState {
  // 2번 상품의 이름 두 칸은 입력칸도 컬럼도 없다 - 빈 값으로 둔다.
  const second: PrizeProductForm = {
    barcode: p.barcode2 ?? '',
    companyName: '',
    productName: '',
  };
  return {
    grade: p.grade && GRADES.includes(p.grade) ? p.grade : GRADES[0]!,
    unit: p.unit && UNITS.includes(p.unit) ? p.unit : UNITS[0]!,
    quantity: p.quantity !== null && p.quantity !== undefined ? String(p.quantity) : '0',
    quantity2: p.quantity2 !== null && p.quantity2 !== undefined ? String(p.quantity2) : '0',
    soldout: p.soldoutFlag === '1' || p.soldoutFlag === 'Y',
    prizeCode: p.prizeCode ?? '',
    goodsIdx: p.goodsIdx !== null && p.goodsIdx !== undefined ? String(p.goodsIdx) : '',
    thumbnail: p.thumbnail ?? '',
    displayImg: p.displayImg ?? '',
    // 기존 경품(2번 상품이 없는 243행)은 입력 행 하나로 열린다 - 지금까지와 같다.
    products: [
      {
        barcode: p.barcode ?? '',
        companyName: p.companyName ?? '',
        productName: p.productName ?? '',
      },
      ...(hasProductValue(second) ? [second] : []),
    ],
  };
}

function toPrizeInput(form: PrizeFormState): RbPrizeInput {
  const [first = EMPTY_PRODUCT, second = EMPTY_PRODUCT] = form.products;
  return {
    grade: form.grade,
    unit: form.unit,
    quantity: form.soldout ? 0 : Number(form.quantity) || 0,
    soldoutFlag: form.soldout ? '1' : '0',
    prizeCode: form.prizeCode,
    goodsIdx: form.goodsIdx === '' ? null : Number(form.goodsIdx),
    thumbnail: form.thumbnail,
    displayImg: form.displayImg,
    barcode: first.barcode,
    companyName: first.companyName,
    productName: first.productName,
    // 2번 상품을 안 넣었거나 지웠으면 빈 문자열이 간다 - 그래야 저장할 때
    // 남아 있던 값이 함께 비워진다. 이름 두 칸은 컬럼째 없어서 안 보낸다
    // (migrations/019).
    barcode2: second.barcode,
    // 2번 상품이 없으면 재고도 null 로 비운다 - 쓰지 않는 칸에 0 이 남아 있으면
    // "상품은 없는데 수량은 0" 처럼 보여 품절 판정을 헷갈리게 한다.
    quantity2: hasProductValue(second) ? (form.soldout ? 0 : Number(form.quantity2) || 0) : null,
  };
}

/** 경품 수정/복사 모달. 원본 prize_modify.php · prize_copy.php 를 합쳤다. */
function PrizeFormModal({
  mode,
  randomboxId,
  prize,
  onClose,
  onSaved,
}: {
  mode: 'edit' | 'copy';
  randomboxId: number;
  prize: RbPrize;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<PrizeFormState>(() => toPrizeFormState(prize));

  const [submitted, setSubmitted] = useState(false);
  /** 규칙은 서버 필드 정의(randombox/index.ts 의 RANDOMBOX_PRIZE_FIELDS)와 같은 값이다. */
  const errors: FieldErrors = {
    prizeCode: checkLen(form.prizeCode, 20, '상품 코드'),
    goodsIdx: checkLen(form.goodsIdx, 64, '실제상품코드'),
    thumbnail: checkImageUrl(form.thumbnail),
    displayImg: checkImageUrl(form.displayImg),
    // 상품 행마다 따로 본다 - 2번 상품의 오류가 1번 칸에 붙으면 안 된다.
    ...Object.fromEntries(
      form.products.flatMap((p, i) => [
        [`barcode${i}`, checkLen(p.barcode, PRODUCT_LIMITS.barcode, '바코드')],
        // 2번 상품은 바코드만 입력한다 - 이름 칸이 없으니 1번 상품만 검사한다.
        // (검사를 남겨두면 화면에 없는 칸의 오류가 저장을 막을 수 있다.)
        ...(i === 0
          ? [
              [`companyName${i}`, checkLen(p.companyName, PRODUCT_LIMITS.companyName, '제작업체명')],
              [`productName${i}`, checkLen(p.productName, PRODUCT_LIMITS.productName, '상품명')],
            ]
          : []),
      ]),
    ),
  };
  const shown: FieldErrors = submitted ? errors : {};

  function setProduct(index: number, patch: Partial<PrizeProductForm>) {
    setForm((f) => ({
      ...f,
      products: f.products.map((p, i) => (i === index ? { ...p, ...patch } : p)),
    }));
  }

  function addProduct() {
    setForm((f) =>
      f.products.length >= MAX_PRIZE_PRODUCTS
        ? f
        : { ...f, products: [...f.products, { ...EMPTY_PRODUCT }] },
    );
  }

  function removeProduct(index: number) {
    setForm((f) => ({ ...f, products: f.products.filter((_, i) => i !== index) }));
  }

  const save = useMutation({
    mutationFn: () => {
      const body = toPrizeInput(form);
      return mode === 'edit' && prize.prizeId !== null
        ? randomboxApi.updatePrize(randomboxId, prize.prizeId, body)
        : randomboxApi.createPrize(randomboxId, body);
    },
    onSuccess: onSaved,
  });

  const valid = form.grade && form.products[0]?.productName;
  // 2번 상품 입력 행에 바코드가 들어 있으면 상품이 둘인 경품이다 - 그때만 2번
  // 수량 칸을 띄운다(없는 상품의 재고를 넣게 두지 않는다).
  const hasSecond = hasProductValue(form.products[1] ?? EMPTY_PRODUCT);

  return (
    <Modal title={mode === 'edit' ? '경품 수정' : '경품 복사'} onClose={onClose} wide>
      {mode === 'copy' && (
        <p className="hint">
          <b>{text(prize.productName)}</b>의 내용을 그대로 새 경품으로 등록합니다. 필요하면 아래
          값을 고친 뒤 저장하세요.
        </p>
      )}

        {/*
          .form-grid(그리드) 가 아니라 .form-stack(flex) 을 쓴다. 그리드일 때는
          폭 전체를 차지하는 자식(.row-2/.row-3/.field--full)만 있어도 브라우저
          auto-placement 가 DOM 순서와 다르게 칸을 잡아 필드가 서로 겹쳐 그려졌다
          - 이 폼은 1~3행만 grid-row 를 손으로 박아 막고 있었는데, 그 뒤에 온
          바코드·제작업체명 행은 고정이 없어 상품코드 행과 겹쳤다(입금건 수정
          모달에서 먼저 겪은 것과 같은 버그. BankPage.test.tsx 회귀 테스트 참고).
          flex 열은 자리 계산 여지가 없어 항상 DOM 순서대로 쌓인다.
        */}
        <form
          className="form-stack prize-form"
          onSubmit={(e) => {
            e.preventDefault();
            setSubmitted(true);
            if (hasFieldErrors(errors)) return;
            save.mutate();
          }}
        >
          <div className="row-3 prize-form__row--first">
            <Field label="등급">
              <select
                className="input"
                value={form.grade}
                onChange={(e) => setForm((f) => ({ ...f, grade: e.target.value }))}
              >
                {GRADES.map((g) => (
                  <option key={g} value={g}>
                    {g} 등급
                  </option>
                ))}
              </select>
            </Field>
            <Field label="유닛">
              <select
                className="input"
                value={form.unit}
                onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
              >
                {UNITS.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            </Field>
            {/* 상품이 둘이면 수량 칸 이름도 상품별로 갈라 준다(2026-10-02). */}
            <Field label={hasSecond ? '상품 1 수량 (개)' : '수량 (개)'}>
              <div className="qty-input-row">
                {/*
                  이 칸 안에는 입력이 둘(수량 + 품절 체크) 있어서 <label> 의 암묵적
                  연결만으로는 어느 쪽 이름인지 가려지지 않는다 - 수량 쪽에 이름을
                  직접 달아 준다.
                */}
                <input
                  className="input"
                  type="number"
                  min={0}
                  aria-label={hasSecond ? '상품 1 수량 (개)' : '수량 (개)'}
                  value={form.quantity}
                  disabled={form.soldout}
                  onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
                />
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={form.soldout}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, soldout: e.target.checked, quantity: e.target.checked ? '0' : f.quantity }))
                    }
                  />
                  품절
                </label>
              </div>
            </Field>
          </div>

          {hasSecond && (
            <div className="row-2 prize-form__row--second">
              <Field label="상품 2 수량 (개)">
                <input
                  className="input"
                  type="number"
                  min={0}
                  value={form.quantity2}
                  disabled={form.soldout}
                  onChange={(e) => setForm((f) => ({ ...f, quantity2: e.target.value }))}
                />
              </Field>
              <p className="hint">
                상품이 둘인 경품은 <b>두 상품 모두 재고가 있어야</b> 판매됩니다 - 하나라도 0 이면
                재고 갱신 배치가 이 경품을 품절로 바꿉니다.
              </p>
            </div>
          )}

          <div className="row-2 prize-form__row--second">
            <Field label="상품 코드" error={shown.prizeCode}>
              <input
                className={inputCls(shown.prizeCode)}
                value={form.prizeCode}
                maxLength={20}
                placeholder="예: 2-2"
                onChange={(e) => setForm((f) => ({ ...f, prizeCode: e.target.value }))}
              />
            </Field>
            <Field label="실제상품코드" error={shown.goodsIdx}>
              <input
                className={inputCls(shown.goodsIdx)}
                value={form.goodsIdx}
                maxLength={64}
                placeholder="예: 2482681"
                onChange={(e) => setForm((f) => ({ ...f, goodsIdx: e.target.value.replace(/[^0-9]/g, '') }))}
              />
            </Field>
          </div>

          <div className="prize-form__row--third">
            <Field label="당첨용 이미지 URL" error={shown.thumbnail} full>
              <div className="img-input-row">
                <input
                  className={inputCls(shown.thumbnail)}
                  value={form.thumbnail}
                maxLength={255}
                  placeholder="https://..."
                  onChange={(e) => setForm((f) => ({ ...f, thumbnail: e.target.value }))}
                />
                {form.thumbnail && <img className="thumb thumb--lg" src={form.thumbnail} alt="" />}
              </div>
            </Field>
          </div>

          <Field label="설명용 이미지 URL" error={shown.displayImg} full>
            <div className="img-input-row">
              <input
                className={inputCls(shown.displayImg)}
                value={form.displayImg}
                maxLength={255}
                placeholder="https://..."
                onChange={(e) => setForm((f) => ({ ...f, displayImg: e.target.value }))}
              />
              {form.displayImg && <img className="thumb thumb--lg" src={form.displayImg} alt="" />}
            </div>
          </Field>

          {/*
            바코드 / 제작업체명 / 상품명 은 폼 **맨 아래**에 모아 둔다. 경품
            자체의 정보(등급·수량·이미지)를 먼저 정하고, 그 칸을 채우는 실물
            상품은 마지막에 붙이는 순서다.

            한 경품에 상품을 최대 2개까지 넣는다 (migrations/018). '+' 를 누르면
            **바코드 한 칸만** 한 줄 더 생기고, 2개가 되면 '+' 는 사라진다 -
            2번 상품은 바코드로만 등록한다 (PrizeProductForm 주석 참고).
          */}
          <fieldset className="prize-products">
            <legend className="prize-products__legend">상품 정보</legend>

            {form.products.map((product, i) => (
              <div className="prize-products__row" key={i}>
                <Field label={`상품 ${i + 1} 바코드`} error={shown[`barcode${i}`]}>
                  <input
                    className={inputCls(shown[`barcode${i}`])}
                    value={product.barcode}
                    maxLength={PRODUCT_LIMITS.barcode}
                    placeholder="예: 83882469899"
                    onChange={(e) => setProduct(i, { barcode: e.target.value })}
                  />
                </Field>
                {/* 제작업체명·상품명은 1번 상품만 입력한다. 2번 상품은 바코드만 받는다. */}
                {i === 0 && (
                  <>
                    <Field label={`상품 ${i + 1} 제작업체명`} error={shown[`companyName${i}`]}>
                      <input
                        className={inputCls(shown[`companyName${i}`])}
                        value={product.companyName}
                        maxLength={PRODUCT_LIMITS.companyName}
                        placeholder="예: 매직아이즈"
                        onChange={(e) => setProduct(i, { companyName: e.target.value })}
                      />
                    </Field>
                    <Field label={`상품 ${i + 1} 상품명`} error={shown[`productName${i}`]}>
                      <input
                        className={inputCls(shown[`productName${i}`])}
                        value={product.productName}
                        maxLength={PRODUCT_LIMITS.productName}
                        placeholder="상품명을 입력하세요..."
                        onChange={(e) => setProduct(i, { productName: e.target.value })}
                      />
                    </Field>
                  </>
                )}
                {/* 1번 상품은 경품의 본래 상품이라 지울 수 없다. */}
                {i > 0 && (
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm prize-products__remove"
                    onClick={() => removeProduct(i)}
                  >
                    상품 {i + 1} 삭제
                  </button>
                )}
              </div>
            ))}

            {form.products.length < MAX_PRIZE_PRODUCTS ? (
              <button type="button" className="btn btn--sm prize-products__add" onClick={addProduct}>
                <span aria-hidden="true">＋</span> 상품 추가
              </button>
            ) : (
              <p className="prize-products__limit">
                상품은 최대 {MAX_PRIZE_PRODUCTS}개까지 등록할 수 있습니다.
              </p>
            )}
          </fieldset>

          {/* 오류 모양은 화면 안에서 한 가지만 쓴다. */}
          {save.isError && <ErrorBox error={save.error} />}

          <button type="submit" className="btn btn--primary btn--block" disabled={!valid || save.isPending}>
            {save.isPending ? '저장 중…' : mode === 'edit' ? '저장하기' : '복사본 만들기'}
          </button>
      </form>
    </Modal>
  );
}
